import React, { useState, useEffect, useRef } from "react";
import styles from "../scss/ReviewsCarousel.module.scss";
import ReviewBox from "./ReviewBox";

const ReviewsCarousel = ({ reviews, showControls }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const carouselRef = useRef(null);
  const slideInterval = useRef(null);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Determine items per slide based on screen width
  const itemsPerSlide = screenWidth >= 1400 ? 4 
                    : screenWidth >= 1200 ? 3 
                    : screenWidth >= 768 ? 2 
                    : 1;

  const totalSlides = Math.ceil(reviews.length / itemsPerSlide);

  // Reset autoplay interval whenever manually sliding
  const resetInterval = () => {
    if (slideInterval.current) clearInterval(slideInterval.current);
    slideInterval.current = setInterval(() => {
      nextSlide();
    }, 5000);
  };

  useEffect(() => {
    resetInterval(); // Ensure interval is reset on slide change
    return () => {
      if (slideInterval.current) clearInterval(slideInterval.current);
    };
  }, [currentSlide, totalSlides]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  // Handle drag start
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartPos(e.clientX);
    if (carouselRef.current) {
      carouselRef.current.style.transition = "none";
    }
  };

  // Handle dragging motion
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const currentPos = e.clientX;
    setTranslateX(currentPos - startPos);
  };

  // Handle drag end (either slide to the next/prev or snap back)
  const handleMouseUp = () => {
    setIsDragging(false);
    if (Math.abs(translateX) > 100) {
      if (translateX < 0) nextSlide();
      else prevSlide();
    }
    setTranslateX(0);
    if (carouselRef.current) {
      carouselRef.current.style.transition = "transform 0.5s ease-in-out";
    }
    resetInterval();
  };

  // Mobile touch events
  const handleTouchStart = (e) => handleMouseDown(e.touches[0]);
  const handleTouchMove = (e) => handleMouseMove(e.touches[0]);
  const handleTouchEnd = () => handleMouseUp();

  return (
    <div
      className={styles.carousel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={() => isDragging && handleMouseUp()}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div
        className={styles.carouselWrapper}
        ref={carouselRef}
        style={{
          transform: `translateX(calc(-${currentSlide * 100}% + ${translateX}px))`,
        }}
      >
        {reviews.map((review, index) => {
          if (index % itemsPerSlide === 0) {
            const slideItems = reviews.slice(index, index + itemsPerSlide);
            return (
              <div className={styles.carouselSlide} key={index}>
                {slideItems.map((item) => (
                  <div className={styles.reviewBox} key={item.name}>
                    <ReviewBox
                      name={item.name}
                      text={item.text}
                      color={item.color}
                    />
                  </div>
                ))}
              </div>
            );
          }
          return null;
        })}
      </div>

      {/* Navigation Arrows */}
      {showControls && (
        <>
          <button className={styles.carouselControlPrev} onClick={prevSlide}>
            ‹
          </button>
          <button className={styles.carouselControlNext} onClick={nextSlide}>
            ›
          </button>
        </>
      )}
    </div>
  );
};

export default ReviewsCarousel;
